import { useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { useTranslation } from 'react-i18next';
import { createBrand, deleteBrand, getBrands, updateBrand } from '../../services/brands';
import { QueryKey } from '../../enums/QueryKey';
import { Brand, BrandEditForm, BrandForm } from '../../types/Brand.interface';
import { useCreateRecord, useDeleteRecord, useUpdateRecord } from './crudOperations';

export function useBrands(params: { withDataStats?: boolean } = {}) {
    const { data, ...rest } = useQuery({
        queryKey: [QueryKey.Brands, params],
        queryFn: () => getBrands(params),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    return { data: data || [], ...rest };
}

export function useUpdateBrand({ onSettled }: UseMutationOptions<Brand, Error, BrandEditForm>) {
    const { t } = useTranslation();

    return useUpdateRecord<Brand, Error, BrandEditForm>({
        updateRecordFn: updateBrand,
        fetchRecordsQueryKey: [QueryKey.Brands],
        recordName: t('brand.public'),
        onSettled,
    });
}

export function useCreateBrand({ onSettled }: UseMutationOptions<Brand, Error, BrandForm>) {
    const { t } = useTranslation();

    return useCreateRecord<Brand, Error, BrandForm>({
        createRecordFn: createBrand,
        fetchRecordsQueryKey: [QueryKey.Brands],
        recordName: t('brand.public'),
        onSettled,
    });
}

export function useDeleteBrand({ onSettled }: UseMutationOptions<{ success: boolean }, Error, number>) {
    const { t } = useTranslation();

    return useDeleteRecord<{ success: boolean }, Error, number>({
        deleteRecordFn: deleteBrand,
        fetchRecordsQueryKey: [QueryKey.Brands],
        recordName: t('brand.public'),
        onSettled,
    });
}
